export const translations = {
  en: {
    // Header & Navigation
    nav: {
      brand: "Nandan",
      work: "Work",
      operatingModel: "Operating Model",
      buildLab: "Build Lab",
      experience: "Experience",
      talk: "Let's talk",
      back: "Back",
      pageLabels: {
        contact: "Contact",
        experience: "Experience",
        privacy: "Privacy Policy",
        terms: "Terms & Conditions",
        caseStudy: "Case Study",
      },
    },

    // Footer
    footer: {
      home: "Home",
      selectedWork: "Selected Work",
      buildLab: "Build Lab",
      experience: "Experience",
      contact: "Contact",
      resume: "Resume",
      medium: "Medium",
      privacyPolicy: "Privacy Policy",
      termsCondition: "Terms & Conditions",
      navigate: "Navigate",
      resources: "Resources",
      legal: "Legal",
      tagline: "Data PM and AI-native builder turning complex workflows into adopted products.",
      rightsReserved: "All rights reserved.",
      bottomTagline: "Built for data, AI, and real-world adoption.",
    },

    // Home / Portfolio Page
    home: {
      seo: {
        title: "Nandan Priyadarshi | Data PM, AI-native Builder and FDE",
        description: "Data Product Manager and AI-native builder who works directly with users, models the data, ships production workflows, and measures adoption.",
        keywords: "Nandan Priyadarshi, Data Product Manager, AI Product Manager, AI-native Builder, Forward Deployed Engineer, FDE, Solo Builder, Data Products",
      },
      hero: {
        badge: "Data PM / AI-native / Forward-deployed / Solo builder",
        headline: "I build AI products teams actually adopt.",
        subheading: "I work with users, model the data, build the workflow, and ship production-ready tools.",
        viewWork: "View selected work",
        downloadResume: "Download resume",
        workbench: {
          title: "Product workbench",
          sub: "Problem to production",
          tag: "AI-native",
          outcome: "Outcome",
          adoptedWorkflow: "Adopted workflow",
          nodes: [
            { label: "User workflow", detail: "Observe the constraint", stack: "", stage: "workflow" },
            { label: "Trusted data", detail: "Define the signal", stack: "PostgreSQL / dbt / Snowflake", stage: "data" },
            { label: "AI system", detail: "Build and evaluate", stack: "OpenAI / Python / FastAPI", stage: "ai" },
            { label: "Shipped product", detail: "Deploy and measure", stack: "React / TypeScript / Vercel", stage: "ship" },
          ],
        },
      },
      ticker: {
        title: "Impact across shipped systems",
        feed: "Impact feed",
        outcomes: [
          { value: "10+", label: "Years building products", note: "Data, AI, platforms" },
          { value: "25+", label: "Products shipped", note: "Zero to one and scale" },
          { value: "5M+", label: "Users reached", note: "Internal and external" },
          { value: "40%", label: "Support time reduced", note: "AI workflow outcome" },
          { value: "70%", label: "L1 self-serve resolution", note: "RAG support workflow" },
          { value: "75%", label: "Critical incidents reduced", note: "Automated data quality" },
          { value: "100%", label: "Leadership adoption", note: "Unified KPI platform" },
          { value: "6 wks", label: "Time to first revenue", note: "Solo-built SaaS" },
        ],
      },
      capabilitiesTitle: "What I bring",
      activeSystem: "Active system",
      evidence: "Evidence",
      proofPrefix: "Proof: ",
      capabilities: [
        {
          title: "Data Product Management",
          description: "I define trusted metrics, shape semantic layers, and turn fragmented reporting into products teams can operate from.",
          proof: "Unified KPI platform with leadership-wide adoption",
          displayTitle: "Turn data into a shared operating language.",
          narrative: "I align definitions, ownership, and product decisions so teams can trust and act on the same signal.",
          signal: "Data product system",
          flow: ["Sources", "Semantics", "Decisions"],
        },
        {
          title: "AI-native Product Development",
          description: "I design RAG and agent workflows around evaluation, guardrails, human review, cost, and real operating constraints.",
          proof: "50K+ monthly queries with 70% L1 self-serve resolution",
          displayTitle: "Build AI around the workflow, not the demo.",
          narrative: "I treat the model as one component in a reliable product with evaluation, guardrails, and human review.",
          signal: "AI product system",
          flow: ["Context", "Evaluate", "Assist"],
        },
        {
          title: "Forward-deployed Execution",
          description: "I work inside user workflows, connect product decisions to system realities, and stay through deployment and adoption.",
          proof: "Embedded IT business partner for South Asia commercial operations",
          displayTitle: "Stay close until the product is adopted.",
          narrative: "I work inside the user's environment, connect the systems, and own the last mile from deployment to behavior change.",
          signal: "Forward-deployed loop",
          flow: ["Embed", "Integrate", "Adopt"],
        },
        {
          title: "Solo Building",
          description: "I scope, prototype, code, and publish working tools with AI-assisted development across TypeScript, JavaScript, and Python.",
          proof: "Public AI, data, and developer-tool projects on GitHub",
          displayTitle: "Shorten the distance from strategy to software.",
          narrative: "I prototype and ship the product myself when a working artifact is the fastest way to learn.",
          signal: "Solo build loop",
          flow: ["Scope", "Build", "Ship"],
        },
      ],
      selectedWork: {
        eyebrow: "Selected work",
        title: "Products that moved from ambiguity to impact.",
        subtitle: "Four stories across AI, analytics, data reliability, and solo product development.",
        readCaseStudy: "Read case study",
      },
      buildLab: {
        title: "Build Lab",
        subtitle: "Public experiments where I learn by building. These repositories show the work, not just the job title.",
        viewGithub: "View GitHub profile",
        repo: "Repository",
      },
      aiPrinciples: {
        title: "How I ship responsible AI products.",
        subtitle: "AI-native means designing the entire operating system around the model, including failure, feedback, and adoption.",
        principles: [
          {
            number: "01",
            title: "Start with the workflow",
            description: "The model is one component. The product starts with the decision or task a user needs to complete.",
          },
          {
            number: "02",
            title: "Evaluate before scaling",
            description: "Define quality, safety, latency, and cost thresholds before expanding traffic or autonomy.",
          },
          {
            number: "03",
            title: "Design the human handoff",
            description: "Make uncertainty visible and give people a clear path to review, correct, or escalate an output.",
          },
          {
            number: "04",
            title: "Measure adoption in context",
            description: "Track whether the product improves the workflow, not only whether the AI produced an answer.",
          },
        ],
      },
      testimonials: {
        title: "Trusted across product, data, and go-to-market teams.",
        items: [
          {
            name: "Todd Schecter",
            title: "Databricks Consulting Growth Leader",
            quote: "Exceptional product leader. Breathtaking pace of innovation. Always finds time to engage with sales and strategic customers.",
          },
          {
            name: "Thangadurai Elangovan",
            title: "AGM and Tech Lead, Tradebees Beijing",
            quote: "Highly skilled in big data. Proactive problem-solver. Calm under pressure and a true asset to the team.",
          },
          {
            name: "Lola Han Luo",
            title: "Senior Product Manager, FlashIntel",
            quote: "The word dedication personified. Energetic, with excellent product management skills. Proud of what we achieved together.",
          },
        ],
      },
      contactCta: {
        eyebrow: "Have a difficult product problem?",
        title: "Let’s turn the workflow into something people can use.",
        button: "Let’s talk",
      },
    },

    // Product System Map Component
    productSystemMap: {
      eyebrow: "Forward-deployed operating model",
      title: "From messy problem to adopted product.",
      subtitle: "I stay close to the user, the data, and the implementation from discovery through production.",
      systemLabel: "Product system",
      systemSub: "Problem to production",
      stages: [
        {
          label: "Embed",
          title: "Start inside the real workflow",
          description: "Interview users, observe the handoffs, and identify the constraints that a requirements document misses.",
          detail: "Users + workflow",
        },
        {
          label: "Model",
          title: "Make the data trustworthy",
          description: "Trace the source, define the metric, and align the semantic model before automating decisions on top of it.",
          detail: "SQL + metrics",
        },
        {
          label: "Build",
          title: "Turn ambiguity into a working product",
          description: "Prototype the interface and AI workflow myself, then use the prototype to accelerate alignment and engineering.",
          detail: "Prototype + AI",
        },
        {
          label: "Deploy",
          title: "Fit the solution into production",
          description: "Connect existing systems, add evaluation and guardrails, and design the operational path around the product.",
          detail: "Integration + reliability",
        },
        {
          label: "Learn",
          title: "Measure adoption, not applause",
          description: "Track usage and business outcomes, return to users, and iterate until the product becomes part of the workflow.",
          detail: "Adoption + impact",
        },
      ],
    },

    // Tech Constellation Component
    techConstellation: {
      ariaLabel: "Technologies used across the product system",
      data: "Trusted data",
      ai: "AI system",
      ship: "Shipped product",
      technologySuffix: "technology",
    },

    // Work Experience Page
    experiencePage: {
      seo: {
        title: "Work Experience - Nandan Priyadarshi | 10+ Years in Product Management",
        description: "10+ years in AI, data and B2B SaaS product management: Xiaomi, FlashLabs, Wemero, TradeBees and Wipro. 100% adoption across 5 markets, 280% transaction growth.",
        keywords: "Product Manager Experience, Senior PM Career, Xiaomi Product Manager, AI Product Development, Product Management Portfolio",
      },
      availableBadge: "Available for Opportunities",
      titleLine1: "A Decade of Building",
      titleLine2: "Products That Matter",
      subtitle: "From data engineering foundations to leading AI-powered commercial platforms — every role shaped my approach to shipping with impact.",
      stats: [
        { label: "Years Experience", value: "10+" },
        { label: "Products Shipped", value: "25+" },
        { label: "Users Impacted", value: "5M+" },
        { label: "Companies", value: "7" },
      ],
      timelineTitle: "Journey Timeline",
      searchPlaceholder: "Search by company, role, or keywords...",
      filterBy: "Filter by expertise",
      allFilter: "All",
      showingCount: "Showing {filtered} of {total} experiences",
      clearFilters: "Clear filters",
      noResults: "No experiences found",
      adjustFilters: "Try adjusting your search or filters",
      card: {
        current: "Current",
        keyMetrics: "Key Metrics",
      },
    },

    // Work Experience Detail Page
    experienceDetail: {
      back: "Back",
      currentRole: "Current Role",
      keyImpact: "Key Impact",
      measurableOutcomes: "Measurable outcomes",
      overview: "Overview",
      keyAchievements: "Key Achievements",
      impactDelivered: "Impact delivered",
      techStack: "Tech Stack & Systems",
      toolsUsed: "Tools & technologies used",
      challengesTackled: "Challenges Tackled",
      problemsSolved: "Problems solved",
      businessOutcomes: "Business Outcomes",
      resultsDelivered: "Results delivered",
      previous: "Previous",
      next: "Next",
      notFoundTitle: "Experience Not Found",
      notFoundDesc: "The work experience you're looking for doesn't exist or has been moved.",
      viewAllExperiences: "View All Experiences",
    },

    // Case Study Page
    caseStudy: {
      back: "Back",
      theChallenge: "The Challenge",
      understandingPain: "Understanding the pain",
      theApproach: "The Approach",
      howWeSolvedIt: "How we solved it",
      theImpact: "The Impact",
      resultsDelivered: "Results delivered",
      techStack: "Tech Stack",
      keyLearnings: "Key Learnings",
      previousCaseStudy: "Previous Case Study",
      nextCaseStudy: "Next Case Study",
      interestedWorking: "Interested in working together?",
      letsTalk: "Let's Talk",
      notFoundTitle: "Case Study Not Found",
      notFoundDesc: "The project you're looking for doesn't exist.",
      viewAllProjects: "View All Projects",
    },

    // Contact Page
    contact: {
      seo: {
        title: "Contact Nandan Priyadarshi | Data and AI Product Collaboration",
        description: "Discuss an AI product, data platform, forward-deployed engagement, or product build with Nandan Priyadarshi.",
        keywords: "Contact Nandan Priyadarshi, Data Product Manager, AI Product Manager, Forward Deployed Product Builder",
      },
      eyebrow: "Open a channel",
      title: "Start with the hard part.",
      subtitle: "Share the workflow, data constraint, or AI product decision. I’ll meet you at the problem.",
      directEmail: "Direct email",
      copyEmail: "Copy email address",
      copied: "Email copied",
      usefulFirstMsg: "A useful first message answers",
      location: "Beijing, China",
      workingBrief: "Working brief",
      workingBriefTitle: "Tell me where the workflow breaks.",
      workingBriefSub: "A rough brief is enough. I’ll ask the useful questions.",
      topicLegend: "What are we discussing?",
      topics: [
        { label: "AI product", subject: "AI product strategy" },
        { label: "Data platform", subject: "Data product or platform" },
        { label: "Embedded delivery", subject: "Forward-deployed collaboration" },
        { label: "Solo build", subject: "Product build collaboration" },
      ],
      briefPrompts: [
        { question: "Who is blocked?", detail: "The user, team, or customer in the workflow." },
        { question: "What breaks today?", detail: "The handoff, data constraint, or decision slowing them down." },
        { question: "What should change?", detail: "The outcome that would make the product worth shipping." },
      ],
      nameLabel: "Your name",
      namePlaceholder: "Your name",
      emailLabel: "Your email",
      emailPlaceholder: "you@example.com",
      subjectLabel: "Subject",
      subjectPlaceholder: "What should we unpack?",
      messageLabel: "The working context",
      messagePlaceholder: "Who is blocked, what breaks today, and what should change?",
      privacyNote: "Your message goes directly to my inbox. No newsletter or automated sequence.",
      sendBrief: "Send brief",
      sending: "Sending",
      sentTitle: "Message sent",
      sentDesc: "Thanks for the context. I’ll review the problem and get back to you.",
      sendAnother: "Send another brief",
      errorTitle: "Something went wrong",
      errorDesc: "Try again, or use the direct email channel instead.",
      tryAgain: "Try again",
      emailDirectly: "Email directly",
    },

    // Legal Pages
    privacy: {
      seo: {
        title: "Privacy Policy | Nandan Priyadarshi",
        description: "Privacy policy for Nandan Priyadarshi's portfolio website. Learn how we collect, use, and protect your personal information.",
        keywords: "Privacy Policy, Data Protection, Nandan Priyadarshi",
      },
      eyebrow: "Legal",
      title: "Privacy Policy",
      lastUpdated: "Last updated: March 2024",
      description: "Your privacy matters to us. Here's how we handle your data.",
      intro: "This Privacy Policy describes how Nandan Priyadarshi (\"we,\" \"us,\" or \"our\") collects, uses, and shares information about you when you visit our portfolio website. By using our website, you agree to the collection and use of information in accordance with this policy.",
      footerNote: "This privacy policy is effective as of January 1, 2026 and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.",
      sections: [
        {
          title: "Information We Collect",
          content: "We collect information you provide directly, such as when you contact us through the contact form. This may include your name, email address, and message content. We also automatically collect certain information when you visit our website, including your IP address, browser type, and pages visited.",
        },
        {
          title: "How We Use Your Information",
          content: "We use the information we collect to respond to your inquiries, improve our website and services, send you updates if you've opted in, and analyze website traffic and usage patterns. We do not sell your personal information to third parties.",
        },
        {
          title: "Data Security",
          content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.",
        },
        {
          title: "Cookies & Tracking",
          content: "Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can control cookies through your browser settings. We use analytics tools to understand how visitors interact with our website.",
        },
        {
          title: "Your Rights",
          content: "You have the right to access, correct, or delete your personal information. You may also opt out of receiving marketing communications at any time. To exercise these rights, please contact us using the information provided below.",
        },
        {
          title: "Contact Us",
          content: "If you have any questions about this Privacy Policy or our data practices, please contact us at nandanpriyadarshi298@gmail.com. We will respond to your inquiry within a reasonable timeframe.",
        },
      ],
    },

    terms: {
      seo: {
        title: "Terms & Conditions | Nandan Priyadarshi",
        description: "Terms and conditions for using Nandan Priyadarshi's portfolio website. Please read these terms carefully before using our services.",
        keywords: "Terms and Conditions, Legal Terms, Nandan Priyadarshi",
      },
      eyebrow: "Legal",
      title: "Terms & Conditions",
      lastUpdated: "Last updated: January 2026",
      description: "Please read these terms carefully before using our website.",
      intro: "Welcome to Nandan Priyadarshi's portfolio website. These terms and conditions outline the rules and regulations for the use of this website. By accessing this website, we assume you accept these terms and conditions in full. Do not continue to use this website if you do not accept all of the terms and conditions stated on this page.",
      footerNote: "These terms and conditions are effective as of January 1, 2026 and will remain in effect except with respect to any changes in its provisions in the future.",
      agreementTitle: "Agreement to Terms",
      agreementText: "By using this website, you signify your acceptance of these terms. If you do not agree to these terms, please do not use our website. Your continued use of the website following the posting of changes to these terms will be deemed your acceptance of those changes.",
      questionsContact: "If you have any questions about these Terms & Conditions, please contact us at",
      sections: [
        {
          title: "Use of Website",
          content: "By accessing this website, you agree to use it only for lawful purposes and in a way that does not infringe the rights of others or restrict their use and enjoyment of the website. You may view, download, and print content from this website for your personal, non-commercial use only.",
        },
        {
          title: "Intellectual Property",
          content: "All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Nandan Priyadarshi and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without prior written consent.",
        },
        {
          title: "Disclaimer",
          content: "The information provided on this website is for general informational purposes only. While we strive to keep the information up to date and accurate, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or availability of the website or the information contained on it.",
        },
        {
          title: "Limitation of Liability",
          content: "In no event shall Nandan Priyadarshi be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of this website. This limitation applies regardless of whether the damages arise from breach of contract, tort, or any other legal theory.",
        },
        {
          title: "Changes to Terms",
          content: "We reserve the right to modify these terms and conditions at any time without prior notice. Your continued use of the website following any changes constitutes acceptance of the new terms. We encourage you to review this page periodically for updates.",
        },
        {
          title: "Governing Law",
          content: "These terms and conditions are governed by and construed in accordance with applicable laws, and you irrevocably submit to the exclusive jurisdiction of the courts in that location. Any disputes arising from these terms will be resolved through appropriate legal channels.",
        },
      ],
    },

    // 404 Page
    notFound: {
      seo: {
        title: "404 - Page Not Found | Nandan Priyadarshi",
        description: "The page you're looking for doesn't exist. Return to Nandan Priyadarshi's portfolio homepage.",
      },
      title: "Page Not Found",
      description: "The page you're looking for doesn't exist or has been moved. Let's get you back on track.",
      backHome: "Back to Home",
      contactMe: "Contact Me",
      quickLinks: "Quick Links:",
      home: "Home",
      experience: "Experience",
      contact: "Contact",
      privacy: "Privacy",
    },

    // Command Palette
    commandPalette: {
      placeholder: "Type a command or search...",
      noResults: "No commands found",
      navigate: "Navigate",
      select: "Select",
      close: "Close",
      commands: {
        home: "Go to Home",
        experience: "View Experience",
        contact: "Contact Me",
        resume: "Download Resume",
        github: "Open GitHub",
        linkedin: "Open LinkedIn",
        twitter: "Open X (Twitter)",
      },
    },

    // Error Boundary
    errorBoundary: {
      title: "Oops! Something went wrong",
      description: "Don't worry, it's not your fault. We've been notified and will fix this soon.",
      technicalDetails: "Technical Details (Dev Only)",
      tryAgain: "Try Again",
      goHome: "Go Home",
      helpText: "If this keeps happening, please",
      contactMe: "contact me",
    },
  },

  zh: {
    // Header & Navigation
    nav: {
      brand: "Nandan",
      work: "精选项目",
      operatingModel: "运作体系",
      buildLab: "实验工坊",
      experience: "工作经历",
      talk: "立即沟通",
      back: "返回",
      pageLabels: {
        contact: "联系方式",
        experience: "工作经历",
        privacy: "隐私政策",
        terms: "条款与条件",
        caseStudy: "案例分析",
      },
    },

    // Footer
    footer: {
      home: "首页",
      selectedWork: "精选案例",
      buildLab: "实验工坊",
      experience: "工作经历",
      contact: "联系我",
      resume: "个人简历",
      medium: "Medium 专栏",
      privacyPolicy: "隐私政策",
      termsCondition: "条款与条件",
      navigate: "快捷导航",
      resources: "资源链接",
      legal: "合规条款",
      tagline: "数据产品经理与 AI 原生实践者，致力于将复杂工作流转化为深度采纳的高价值产品。",
      rightsReserved: "版权所有。",
      bottomTagline: "专注于数据中台、AI 系统与业务真实采纳。",
    },

    // Home / Portfolio Page
    home: {
      seo: {
        title: "Nandan Priyadarshi (南丹) | 数据产品经理 · AI 原生构建者 · 前沿交付专家",
        description: "数据产品经理与 AI 原生实践者。深入一线用户工作流，构建可信数据中台，交付生产级 AI 体系并持续度量业务采纳。",
        keywords: "Nandan Priyadarshi, 南丹, 数据产品经理, AI产品经理, AI原生构建, 驻场交付专家, FDE, 独立开发者, 数据中台",
      },
      hero: {
        badge: "数据产品经理 / AI原生构建 / 驻场协同 / 独立全栈",
        headline: "我专注于构建 真正能被业务深度采纳的 AI 产品。",
        subheading: "深入一线调研用户诉求，抽象可信数据模型，沉淀业务工作流，极速交付生产级可用工具。",
        viewWork: "查看精选案例",
        downloadResume: "下载个人简历",
        workbench: {
          title: "产品实验台",
          sub: "从痛点到生产落地",
          tag: "AI 原生",
          outcome: "最终成效",
          adoptedWorkflow: "深度采纳的工作流",
          nodes: [
            { label: "用户工作流", detail: "深入洞察业务制约与痛点", stack: "", stage: "workflow" },
            { label: "可信数据", detail: "统一口径，定义核心指标", stack: "PostgreSQL / dbt / Snowflake", stage: "data" },
            { label: "AI 系统", detail: "模型调优与严格评测护栏", stack: "OpenAI / Python / FastAPI", stage: "ai" },
            { label: "交付上线", detail: "工程投产与真实采纳度量", stack: "React / TypeScript / Vercel", stage: "ship" },
          ],
        },
      },
      ticker: {
        title: "已交付核心系统的量化成效",
        feed: "成效流",
        outcomes: [
          { value: "10+", label: "年产品管理与构建沉淀", note: "数据中台、AI 与核心商业平台" },
          { value: "25+", label: "款成功上线交付产品", note: "跨越 0 到 1 及规模化商业增长" },
          { value: "5M+", label: "深度触达真实用户", note: "服务全球跨国业务与企业内部用户" },
          { value: "40%", label: "支持响应与流转工时缩减", note: "AI 工作流重塑商业成效" },
          { value: "70%", label: "L1 客服一线自主闭环率", note: "RAG 检索增强智能问答系统" },
          { value: "75%", label: "关键数据事故与异常降低", note: "自动化数据治理与可观测体系" },
          { value: "100%", label: "集团高管层全员采纳", note: "统一核心商业 KPI 指标平台" },
          { value: "6 周", label: "跑通从构想到商业化首单", note: "AI 赋能全栈独立孵化 SaaS" },
        ],
      },
      capabilitiesTitle: "核心能力与沉淀",
      activeSystem: "运作体系",
      evidence: "成效佐证",
      proofPrefix: "成效佐证：",
      capabilities: [
        {
          title: "数据产品管理 (Data PM)",
          description: "统一定义可信口径，打造清晰语义层，将零散报表打磨为跨职能团队可直接依凭行动的标准化数据产品。",
          proof: "高管层全面深度采纳的企业级核心 KPI 决策平台",
          displayTitle: "让底层数据成为团队共通的业务运作语言。",
          narrative: "拉通指标定义、数据权属与产品决策，确保全公司依据同一套可靠信号协同前进。",
          signal: "数据产品体系",
          flow: ["数据源头", "统一语义", "业务决策"],
        },
        {
          title: "AI 原生产品研发",
          description: "围绕严密评估、安全护栏、人机协作复核、成本优化及真实业务约束，设计落地 RAG 与智能体工作流。",
          proof: "月均 5万+ 次问答，达成 70% L1 客服自助闭环解决",
          displayTitle: "紧紧围绕真实工作流构建 AI，而非停留于演示 Demo。",
          narrative: "将大模型视为高可靠产品的一个核心组件，配齐完备的质量评测、护栏与人工介入机制。",
          signal: "AI 产品体系",
          flow: ["上下文检索", "可靠评估", "协同辅助"],
        },
        {
          title: "前沿部署驻场交付 (FDE)",
          description: "深入一线业务工作流，将产品决策与底层系统现实紧密贴合，全程主导从开发部署到真实习惯养成的全过程。",
          proof: "驻场深度赋能南亚跨国商业运营的 IT 业务合伙人",
          displayTitle: "紧扎一线现场，直至产品真正被深度采纳。",
          narrative: "深入用户日常工作环境打通断点系统，对‘最后一公里’的部署投产与行为转变切实负责。",
          signal: "前沿交付闭环",
          flow: ["深入现场", "系统集成", "落地采纳"],
        },
        {
          title: "独立全栈研发",
          description: "借助 AI 赋能辅助编程，使用 TypeScript、JavaScript 与 Python 独立完成范围定义、高保真原型与工程交付。",
          proof: "在 GitHub 上公开发布的多项 AI、数据与开发者工具开源项目",
          displayTitle: "极速缩短从业务构想到可用软件的距离。",
          narrative: "当一个切实可用的交互实体是最快的验证手段时，我能亲自原型化并全栈交付上线。",
          signal: "独立研发闭环",
          flow: ["需求范围", "工程构建", "交付上线"],
        },
      ],
      selectedWork: {
        eyebrow: "精选案例",
        title: "从混沌不确定，走向显著商业成效的产品实践。",
        subtitle: "精选 4 个实战复盘：涵盖 AI、指标分析中台、数据质量工程与独立出海孵化。",
        readCaseStudy: "阅读深度复盘",
      },
      buildLab: {
        title: "Build Lab 实验工坊",
        subtitle: "通过亲手构建持续精进的公开开源实验项目。用扎实的代码与架构展现硬核实力，而非止步于职位光环。",
        viewGithub: "访问 GitHub 个人主页",
        repo: "项目仓库",
      },
      aiPrinciples: {
        title: "我如何构建负责任且可靠的 AI 产品。",
        subtitle: "AI 原生意味着围绕大模型设计完整的操作系统，包含失败降级、反馈闭环以及业务采纳。",
        principles: [
          {
            number: "01",
            title: "始于真实工作流",
            description: "大模型仅是底层的一个组件。真正实用的产品始于用户需要闭环完成的业务决策或核心任务。",
          },
          {
            number: "02",
            title: "放量之前，评测先行",
            description: "在扩大调用流量或提升自主权限前，必须先建立严格的质量、安全性、延迟与成本基线门槛。",
          },
          {
            number: "03",
            title: "严谨设计人机协同流转",
            description: "显性化模型输出的不确定性，为用户提供清晰明了的复核、修正或升级求助路径。",
          },
          {
            number: "04",
            title: "在真实业务场景中衡量采纳度",
            description: "不仅关注 AI 是否给出了答案，更持续追踪产品是否切实改进了业务流转效率与成效。",
          },
        ],
      },
      testimonials: {
        title: "深获产研、数据与商业化团队的坚定信赖。",
        items: [
          {
            name: "Todd Schecter",
            title: "Databricks 咨询业务增长负责人",
            quote: "卓越的产品领导者。产品创新推进速度令人惊叹。无论多忙，他总能抽出时间与销售团队紧密配合、深入对接战略级核心客户。",
          },
          {
            name: "Thangadurai Elangovan",
            title: "北京贸易蜂 (Tradebees) 助理总经理兼技术总监",
            quote: "在大数据与平台工程领域技术功底极其扎实。主动积极解决棘手难题。面对重大上线压力冷静沉着，是团队不可多得的核心中坚力量。",
          },
          {
            name: "Lola Han Luo",
            title: "FlashIntel 高级产品经理",
            quote: "他是‘高度专注与敬业’的代名词。精力充沛，拥有极其优秀的产品管理素养与领导力。能与他并肩作战、一同攻坚并达成战绩，我感到非常自豪。",
          },
        ],
      },
      contactCta: {
        eyebrow: "正面临复杂的商业与数据产品难题？",
        title: "让我们携手将繁杂的工作流转化为真正好用的高价值产品。",
        button: "立即沟通",
      },
    },

    // Product System Map Component
    productSystemMap: {
      eyebrow: "前沿交付运作体系",
      title: "从混沌痛点到真正被广泛采纳的产品。",
      subtitle: "从需求探索到生产上线，我始终深扎于用户、数据与工程实现的一线。",
      systemLabel: "产品交付体系",
      systemSub: "从痛点到生产落地",
      stages: [
        {
          label: "深入现场 (Embed)",
          title: "深入真实工作流核心",
          description: "深度访谈一线用户，梳理业务交接链路，精准识别产品需求文档（PRD）往往遗漏的隐性约束。",
          detail: "用户与业务流程",
        },
        {
          label: "数据建模 (Model)",
          title: "让底层数据真实可信",
          description: "追溯数据源头，统一定义核心指标与口径，打磨一致的语义层，为上层智能决策构筑坚实基底。",
          detail: "SQL与指标体系",
        },
        {
          label: "敏捷构建 (Build)",
          title: "把模糊构想打磨为可用产品",
          description: "亲自动手快速搭建可交互原型与 AI 工作流，以切实可见的产品实体加速团队共识与工程落地。",
          detail: "可交互原型与AI工程",
        },
        {
          label: "交付上线 (Deploy)",
          title: "严谨嵌入生产系统与环境",
          description: "打通既有业务系统，引入全流程评测体系与安全护栏，并针对实际业务落地设计闭环运维路径。",
          detail: "系统集成与高可靠护栏",
        },
        {
          label: "迭代复盘 (Learn)",
          title: "以真实业务采用度衡量成效",
          description: "持续追踪产品真实使用率与商业结果，重回用户现场紧密跟进，不断迭代直至产品真正融入日常工作流。",
          detail: "实际采用度与业务增长",
        },
      ],
    },

    // Tech Constellation Component
    techConstellation: {
      ariaLabel: "产品体系所用技术全景概览",
      data: "可信数据",
      ai: "AI系统",
      ship: "交付上线",
      technologySuffix: "技术栈",
    },

    // Work Experience Page
    experiencePage: {
      seo: {
        title: "工作经历 - Nandan Priyadarshi (南丹) | 10+ 年产品管理生涯",
        description: "10 年以上 AI、数据与 B2B SaaS 产品管理经验：小米、FlashLabs、Wemero、TradeBees、Wipro。代表成果：5 个市场 100% 采用、交易量增长 280%。",
        keywords: "产品经理工作经历, 资深产品经理履历, 小米产品经理, AI产品研发, 产品经理作品集",
      },
      availableBadge: "随时可沟通新机会",
      titleLine1: "十年磨一剑",
      titleLine2: "打造真正创造价值的产品",
      subtitle: "从坚实的数据工程与底层架构起步，到全面执掌 AI 赋能的跨国商业中台体系 —— 每一个职位都在锤炼我以商业结果为导向的交付哲学。",
      stats: [
        { label: "从业经验", value: "10+年" },
        { label: "交付产品", value: "25+款" },
        { label: "触达用户", value: "500万+" },
        { label: "服务企业", value: "7家" },
      ],
      timelineTitle: "职业成长轨迹",
      searchPlaceholder: "输入公司名、职位名称或关键词进行搜索...",
      filterBy: "按专业领域筛选",
      allFilter: "全部经历",
      showingCount: "正在展示 {filtered} 条（共 {total} 条）工作经历",
      clearFilters: "清空筛选条件",
      noResults: "未检索到匹配的工作经历",
      adjustFilters: "请尝试调整您的搜索关键词或标签筛选条件",
      card: {
        current: "在职",
        keyMetrics: "核心成效",
      },
    },

    // Work Experience Detail Page
    experienceDetail: {
      back: "返回经历列表",
      currentRole: "目前在任岗位",
      keyImpact: "核心价值成效",
      measurableOutcomes: "可量化业绩指标",
      overview: "岗位综述与职责",
      keyAchievements: "主要业绩与核心成果",
      impactDelivered: "落地业务回报",
      techStack: "技术架构与核心系统",
      toolsUsed: "所用工具、平台与技术栈",
      challengesTackled: "攻坚克难的关键挑战",
      problemsSolved: "化解的痛点与瓶颈",
      businessOutcomes: "商业与组织产出",
      resultsDelivered: "最终交付成果",
      previous: "上一项经历",
      next: "下一项经历",
      notFoundTitle: "未找到该工作经历",
      notFoundDesc: "您所查询的工作经历不存在或已被更新归档。",
      viewAllExperiences: "浏览全部工作经历",
    },

    // Case Study Page
    caseStudy: {
      back: "返回案例列表",
      theChallenge: "核心挑战与业务痛点",
      understandingPain: "深入洞察业务痛点",
      theApproach: "解决方案与推行路径",
      howWeSolvedIt: "拆解执行与技术方案",
      theImpact: "项目成效与业务收益",
      resultsDelivered: "量化指标与成果落地",
      techStack: "技术选型与工具链",
      keyLearnings: "复盘沉淀与核心认知",
      previousCaseStudy: "上一个案例",
      nextCaseStudy: "下一个案例",
      interestedWorking: "期待与您探讨类似业务场景与合作？",
      letsTalk: "立即发起交流",
      notFoundTitle: "未找到该案例",
      notFoundDesc: "您所查找的项目案例不存在或已被迁移。",
      viewAllProjects: "查看全部项目",
    },

    // Contact Page
    contact: {
      seo: {
        title: "联系 Nandan Priyadarshi (南丹) | 数据与 AI 产品深度合作",
        description: "与 Nandan Priyadarshi 探讨 AI 产品、数据平台、前沿交付驻场协同或独立产品孵化合作。",
        keywords: "联系 Nandan Priyadarshi, 数据产品经理, AI 产品经理, 前沿交付工程师, FDE",
      },
      eyebrow: "开启沟通渠道",
      title: "直击最核心的业务难题。",
      subtitle: "无论是具体工作流瓶颈、底层数据约束还是 AI 产品架构决策，我与您直面问题一线。",
      directEmail: "直接联系邮箱",
      copyEmail: "复制邮箱地址",
      copied: "已复制邮箱",
      usefulFirstMsg: "一份高价值的初次沟通往往包含",
      location: "中国 · 北京",
      workingBrief: "协作需求简报",
      workingBriefTitle: "告诉我当前工作流在何处受阻。",
      workingBriefSub: "只需简要描述当前痛点，我会提出针对性的关键问题共同推演。",
      topicLegend: "我们探讨的主题是？",
      topics: [
        { label: "AI 产品", subject: "AI product strategy" },
        { label: "数据平台", subject: "Data product or platform" },
        { label: "现场交付", subject: "Forward-deployed collaboration" },
        { label: "独立研发", subject: "Product build collaboration" },
      ],
      briefPrompts: [
        { question: "谁的流程被阻碍了？", detail: "工作流中受影响的一线用户、业务团队或最终客户。" },
        { question: "当前痛点在何处？", detail: "拖慢运转的系统交接断点、数据质量制约或决策模糊。" },
        { question: "期望实现什么转变？", detail: "让这款产品真正值得投产交付的核心成效与指标提升。" },
      ],
      nameLabel: "您的姓名",
      namePlaceholder: "您的姓名",
      emailLabel: "您的电子邮箱",
      emailPlaceholder: "you@example.com",
      subjectLabel: "讨论主题",
      subjectPlaceholder: "我们该深入探讨什么？",
      messageLabel: "实际业务背景与痛点",
      messagePlaceholder: "谁的流程被阻碍了？当前什么环节脱节？希望达成什么转变？",
      privacyNote: "您的留言将直达我的个人邮箱，绝无垃圾邮件、订阅推销或自动化营销骚扰。",
      sendBrief: "发送需求简报",
      sending: "正在发送...",
      sentTitle: "简报已成功送达",
      sentDesc: "感谢您提供详尽的业务背景。我将认真研读并在短时间内给予回复。",
      sendAnother: "发送另一份简报",
      errorTitle: "发送过程出现异常",
      errorDesc: "请稍后重试，或直接通过个人邮箱联系我。",
      tryAgain: "重新尝试",
      emailDirectly: "直接发送邮件",
    },

    // Legal Pages
    privacy: {
      seo: {
        title: "隐私政策 | Nandan Priyadarshi",
        description: "Nandan Priyadarshi 个人作品集网站的隐私政策说明。了解我们如何依法收集、使用和保护您的个人信息安全。",
        keywords: "隐私政策, 数据保护, Nandan Priyadarshi",
      },
      eyebrow: "合规与声明",
      title: "隐私政策 (Privacy Policy)",
      lastUpdated: "最后更新时间：2026 年 1 月",
      description: "您的隐私对我们至关重要。以下说明我们如何处理您的数据。",
      intro: "本隐私政策说明了 Nandan Priyadarshi（“我们”）在您访问本个人作品集网站时如何收集、使用和共享与您相关的信息。通过使用我们的网站，即表示您同意按照本政策收集和使用相关信息。",
      footerNote: "本隐私政策自 2026 年 1 月 1 日起生效。除非将来对其条款进行任何变更（变更将在本页面发布后立即生效），否则将持续有效。",
      sections: [
        {
          title: "信息收集范围",
          content: "我们仅收集您在主动使用网站功能时提供的信息，例如在‘联系我’表单中所填写的姓名、电子邮箱及留言内容。同时，在您浏览网页期间，系统可能自动收集必要的非敏感技术日志信息（如 IP 地址、浏览器类型、操作系统及页面访问时长等），以便保障网站稳定运行。",
        },
        {
          title: "信息使用目的",
          content: "我们收集此类信息仅用于：回复您的商务咨询与合作意向、持续优化本网站的用户浏览体验、以及进行脱敏的网站流量统计分析。我们郑重承诺，绝不会向任何第三方商业机构出售、出租或不正当共享您的个人隐私数据。",
        },
        {
          title: "数据安全防护",
          content: "我们部署了符合业界通行标准的技术与组织安全防护措施，全力保护您的个人数据免遭未经授权的访问、泄露、篡改或毁损。但鉴于互联网公网通信的固有限制，没有任何数据传输能保证绝对的安全无虞。",
        },
        {
          title: "Cookies 与分析技术",
          content: "本网站可能使用少量基础 Cookies 及类似轻量级分析技术，以记住您的偏好（例如界面语言设置）并帮助我们了解网站的访问热度。您随时可以通过自身浏览器的隐私安全选项控制或禁用 Cookies。",
        },
        {
          title: "您的合法权益",
          content: "依法您享有查询、更正或要求删除您在此前提出的个人信息留言的合法权利。若您希望行使上述权利，欢迎通过本站提供的电子邮箱与我取得联系。",
        },
        {
          title: "联系方式",
          content: "如果您对本隐私政策条款或个人数据保护实践有任何疑问、建议或反馈，请通过邮箱 nandanpriyadarshi298@gmail.com 与我联系。我们将在法定期限内妥善处理并答复您的来信。",
        },
      ],
    },

    terms: {
      seo: {
        title: "条款与条件 | Nandan Priyadarshi",
        description: "使用 Nandan Priyadarshi 个人作品集网站的条款与条件。在访问和使用本网站之前，请仔细阅读本协议。",
        keywords: "服务条款, 使用协议, 法律声明, Nandan Priyadarshi",
      },
      eyebrow: "合规与声明",
      title: "条款与条件 (Terms & Conditions)",
      lastUpdated: "最后更新时间：2026 年 1 月",
      description: "在访问和使用本网站之前，请仔细阅读以下条款与条件。",
      intro: "欢迎访问 Nandan Priyadarshi 的个人作品集网站。本条款与条件概述了使用本网站的规则与规范。访问本网站即表示我们默认您完全接受这些条款与条件。如果您不接受本页面列出的所有条款与条件，请勿继续使用本网站。",
      footerNote: "本条款与条件自 2026 年 1 月 1 日起生效。除非将来对其条款进行任何修订，否则将持续有效。",
      agreementTitle: "同意本条款",
      agreementText: "使用本网站即表示您认可并接受本条款的所有约束。如果您不同意这些条款，请停止使用本网站。在条款发生变更发布后，您继续使用本网站即视为您接受这些变更。",
      questionsContact: "如果您对本条款与条件有任何疑问，请通过以下邮箱与我们联系：",
      sections: [
        {
          title: "网站使用规范",
          content: "访问本网站即表示您同意仅出于合法、正当目的使用本站，不得实施任何侵犯他人权益、扰乱服务器正常运转或限制其他访客正常访问的行为。您仅可出于个人了解与非商业评估目的浏览、查阅本网站展示的公开内容。",
        },
        {
          title: "知识产权归属",
          content: "本网站内呈现的所有文字论述、案例复盘、UI 设计规范、视觉构图、代码逻辑及图标标识等，除明确注明来源或属于相关公司商标外，其知识产权均归 Nandan Priyadarshi 所有。未经书面授权许可，任何人不得擅自抄袭、全盘抓取、商业转载或用于二次发行。",
        },
        {
          title: "免责声明",
          content: "本网站所展示的全部内容及作品案例仅供专业交流与个人背景介绍之用。尽管我们努力确保信息的准确性与时效性，但不对内容的绝对完整性、实时无误性或适用于特定商业意图作出任何明示或默示的担保。",
        },
        {
          title: "责任限制",
          content: "在法律允许的最大限度内，Nandan Priyadarshi 对因访问、使用或无法使用本网站所引起的任何直接、间接、偶然或继发性损失不承担任何法律责任。用户应当自行承担使用本网站内容所带来的客观风险。",
        },
        {
          title: "条款变更权利",
          content: "我们保留在任何时候无需预先通知而适时更新、修订本条款与条件的权利。修订后的条款一经在网站公开发布即行生效。继续访问本站即视为您已充分知晓并同意接受修改后的全部内容。",
        },
        {
          title: "适用法律与管辖",
          content: "本使用条款的订立、生效、履行及纠纷解决均受相关法律法规管辖。因本条款或使用本网站引起的任何争议，各方应首先友好协商解决；协商不成的，应提交具有管辖权的法定裁判机构依法处理。",
        },
      ],
    },

    // 404 Page
    notFound: {
      seo: {
        title: "404 - 页面未找到 | Nandan Priyadarshi",
        description: "很抱歉，您访问的页面不存在。欢迎返回 Nandan Priyadarshi 的作品集主页。",
      },
      title: "页面未找到 (404)",
      description: "您正在查找的页面不存在，或者已经被移动或重新命名。让我们带您回到正确的方向。",
      backHome: "返回网站首页",
      contactMe: "直接联系我",
      quickLinks: "快捷链接导航：",
      home: "首页",
      experience: "工作经历",
      contact: "联系我",
      privacy: "隐私政策",
    },

    // Command Palette
    commandPalette: {
      placeholder: "输入快捷指令或搜索页面...",
      noResults: "未找到匹配的快捷指令",
      navigate: "切换选中",
      select: "确认执行",
      close: "关闭窗口",
      commands: {
        home: "前往主页",
        experience: "查看工作经历",
        contact: "联系我",
        resume: "下载个人简历",
        github: "访问 GitHub 主页",
        linkedin: "访问 LinkedIn 主页",
        twitter: "访问 X (Twitter) 动态",
      },
    },

    // Error Boundary
    errorBoundary: {
      title: "抱歉！页面出现临时异常",
      description: "请不必担心，这并非您的操作所致。我们已收到相关日志并将尽快修复。",
      technicalDetails: "技术堆栈排查信息 (仅限开发模式)",
      tryAgain: "刷新重试",
      goHome: "返回主页",
      helpText: "如果该问题持续出现，欢迎",
      contactMe: "与我取得联系",
    },
  },

  vi: {
    // Header & Navigation
    nav: {
      brand: "Nandan",
      work: "Dự án",
      operatingModel: "Mô hình vận hành",
      buildLab: "Build Lab",
      experience: "Kinh nghiệm",
      talk: "Trò chuyện ngay",
      back: "Quay lại",
      pageLabels: {
        contact: "Liên hệ",
        experience: "Kinh nghiệm",
        privacy: "Chính sách quyền riêng tư",
        terms: "Điều khoản & Điều kiện",
        caseStudy: "Nghiên cứu điển hình",
      },
    },

    // Footer
    footer: {
      home: "Trang chủ",
      selectedWork: "Dự án tiêu biểu",
      buildLab: "Build Lab",
      experience: "Kinh nghiệm",
      contact: "Liên hệ",
      resume: "Sơ yếu lý lịch",
      medium: "Medium",
      privacyPolicy: "Chính sách quyền riêng tư",
      termsCondition: "Điều khoản & Điều kiện",
      navigate: "Điều hướng",
      resources: "Tài nguyên",
      legal: "Pháp lý",
      tagline: "Data PM và nhà xây dựng AI-native, biến những quy trình phức tạp thành sản phẩm được sử dụng thực sự.",
      rightsReserved: "Bảo lưu mọi quyền.",
      bottomTagline: "Xây dựng cho dữ liệu, AI và ứng dụng thực tế.",
    },

    // Home / Portfolio Page
    home: {
      seo: {
        title: "Nandan Priyadarshi | Data PM, nhà xây dựng AI-native và FDE",
        description: "Quản lý sản phẩm dữ liệu và nhà xây dựng AI-native, làm việc trực tiếp với người dùng, mô hình hóa dữ liệu, triển khai quy trình production và đo lường mức độ áp dụng.",
        keywords: "Nandan Priyadarshi, Quản lý sản phẩm dữ liệu, Quản lý sản phẩm AI, Nhà xây dựng AI-native, Forward Deployed Engineer, FDE, Nhà phát triển độc lập, Sản phẩm dữ liệu",
      },
      hero: {
        badge: "Data PM / AI-native / Triển khai thực địa / Nhà xây dựng độc lập",
        headline: "Tôi xây dựng sản phẩm AI mà các đội ngũ thực sự sử dụng.",
        subheading: "Tôi làm việc cùng người dùng, mô hình hóa dữ liệu, xây dựng quy trình và đưa ra những công cụ sẵn sàng cho production.",
        viewWork: "Xem dự án tiêu biểu",
        downloadResume: "Tải sơ yếu lý lịch",
        workbench: {
          title: "Bàn làm việc sản phẩm",
          sub: "Từ vấn đề đến production",
          tag: "AI-native",
          outcome: "Kết quả",
          adoptedWorkflow: "Quy trình được áp dụng",
          nodes: [
            { label: "Quy trình người dùng", detail: "Quan sát ràng buộc", stack: "", stage: "workflow" },
            { label: "Dữ liệu tin cậy", detail: "Xác định tín hiệu", stack: "PostgreSQL / dbt / Snowflake", stage: "data" },
            { label: "Hệ thống AI", detail: "Xây dựng và đánh giá", stack: "OpenAI / Python / FastAPI", stage: "ai" },
            { label: "Sản phẩm ra mắt", detail: "Triển khai và đo lường", stack: "React / TypeScript / Vercel", stage: "ship" },
          ],
        },
      },
      ticker: {
        title: "Tác động từ các hệ thống đã triển khai",
        feed: "Dòng tác động",
        outcomes: [
          { value: "10+", label: "Năm xây dựng sản phẩm", note: "Dữ liệu, AI, nền tảng" },
          { value: "25+", label: "Sản phẩm đã ra mắt", note: "Từ 0 đến 1 và mở rộng quy mô" },
          { value: "5 triệu+", label: "Người dùng tiếp cận", note: "Nội bộ và bên ngoài" },
          { value: "40%", label: "Giảm thời gian hỗ trợ", note: "Kết quả từ quy trình AI" },
          { value: "70%", label: "Tự giải quyết yêu cầu L1", note: "Quy trình hỗ trợ RAG" },
          { value: "75%", label: "Giảm sự cố nghiêm trọng", note: "Chất lượng dữ liệu tự động" },
          { value: "100%", label: "Ban lãnh đạo sử dụng", note: "Nền tảng KPI hợp nhất" },
          { value: "6 tuần", label: "Đến doanh thu đầu tiên", note: "SaaS tự xây dựng" },
        ],
      },
      capabilitiesTitle: "Năng lực của tôi",
      activeSystem: "Hệ thống đang chọn",
      evidence: "Minh chứng",
      proofPrefix: "Minh chứng: ",
      capabilities: [
        {
          title: "Quản lý sản phẩm dữ liệu",
          description: "Tôi định nghĩa các chỉ số đáng tin cậy, định hình lớp ngữ nghĩa và biến hệ thống báo cáo rời rạc thành sản phẩm mà các đội ngũ có thể vận hành dựa trên đó.",
          proof: "Nền tảng KPI hợp nhất được toàn bộ ban lãnh đạo sử dụng",
          displayTitle: "Biến dữ liệu thành ngôn ngữ vận hành chung.",
          narrative: "Tôi thống nhất định nghĩa, quyền sở hữu và quyết định sản phẩm để các đội ngũ có thể tin tưởng và hành động dựa trên cùng một tín hiệu.",
          signal: "Hệ thống sản phẩm dữ liệu",
          flow: ["Nguồn dữ liệu", "Ngữ nghĩa", "Quyết định"],
        },
        {
          title: "Phát triển sản phẩm AI-native",
          description: "Tôi thiết kế quy trình RAG và agent xoay quanh đánh giá chất lượng, cơ chế kiểm soát, rà soát của con người, chi phí và các ràng buộc vận hành thực tế.",
          proof: "Hơn 50.000 truy vấn mỗi tháng với 70% yêu cầu L1 được tự giải quyết",
          displayTitle: "Xây dựng AI quanh quy trình thực, không phải bản demo.",
          narrative: "Tôi xem mô hình là một thành phần trong một sản phẩm đáng tin cậy, đi kèm đánh giá chất lượng, cơ chế kiểm soát và rà soát của con người.",
          signal: "Hệ thống sản phẩm AI",
          flow: ["Ngữ cảnh", "Đánh giá", "Hỗ trợ"],
        },
        {
          title: "Triển khai thực địa",
          description: "Tôi làm việc ngay trong quy trình của người dùng, kết nối quyết định sản phẩm với thực tế hệ thống và đồng hành đến khi sản phẩm được triển khai và áp dụng.",
          proof: "Đối tác kinh doanh CNTT trực tiếp cho hoạt động kinh doanh khu vực Nam Á",
          displayTitle: "Đồng hành đến khi sản phẩm được áp dụng.",
          narrative: "Tôi làm việc trong môi trường của người dùng, kết nối các hệ thống và chịu trách nhiệm chặng cuối, từ triển khai đến thay đổi hành vi.",
          signal: "Vòng lặp triển khai thực địa",
          flow: ["Hòa nhập", "Tích hợp", "Áp dụng"],
        },
        {
          title: "Xây dựng độc lập",
          description: "Tôi xác định phạm vi, làm prototype, viết code và phát hành các công cụ hoạt động thực tế với sự hỗ trợ của AI trên TypeScript, JavaScript và Python.",
          proof: "Các dự án AI, dữ liệu và công cụ lập trình công khai trên GitHub",
          displayTitle: "Rút ngắn khoảng cách từ chiến lược đến phần mềm.",
          narrative: "Tôi tự làm prototype và ra mắt sản phẩm khi một sản phẩm chạy được là cách học nhanh nhất.",
          signal: "Vòng lặp xây dựng độc lập",
          flow: ["Phạm vi", "Xây dựng", "Ra mắt"],
        },
      ],
      selectedWork: {
        eyebrow: "Dự án tiêu biểu",
        title: "Những sản phẩm đi từ mơ hồ đến tác động thực.",
        subtitle: "Bốn câu chuyện về AI, phân tích dữ liệu, độ tin cậy dữ liệu và phát triển sản phẩm độc lập.",
        readCaseStudy: "Đọc nghiên cứu điển hình",
      },
      buildLab: {
        title: "Build Lab",
        subtitle: "Những thử nghiệm công khai nơi tôi học bằng cách tự tay xây dựng. Các kho mã này thể hiện công việc thực tế, không chỉ là một chức danh.",
        viewGithub: "Xem hồ sơ GitHub",
        repo: "Kho mã",
      },
      aiPrinciples: {
        title: "Cách tôi xây dựng sản phẩm AI có trách nhiệm.",
        subtitle: "AI-native nghĩa là thiết kế toàn bộ hệ thống vận hành xoay quanh mô hình, bao gồm cả lỗi, phản hồi và mức độ áp dụng.",
        principles: [
          {
            number: "01",
            title: "Bắt đầu từ quy trình",
            description: "Mô hình chỉ là một thành phần. Sản phẩm bắt đầu từ quyết định hoặc nhiệm vụ mà người dùng cần hoàn thành.",
          },
          {
            number: "02",
            title: "Đánh giá trước khi mở rộng",
            description: "Xác định ngưỡng chất lượng, an toàn, độ trễ và chi phí trước khi tăng lưu lượng hoặc mức độ tự chủ.",
          },
          {
            number: "03",
            title: "Thiết kế điểm chuyển giao cho con người",
            description: "Làm rõ mức độ không chắc chắn và cho người dùng một lộ trình rõ ràng để rà soát, chỉnh sửa hoặc chuyển cấp kết quả.",
          },
          {
            number: "04",
            title: "Đo lường mức độ áp dụng trong bối cảnh thực",
            description: "Theo dõi xem sản phẩm có cải thiện quy trình hay không, chứ không chỉ xem AI có đưa ra câu trả lời hay không.",
          },
        ],
      },
      testimonials: {
        title: "Được tin tưởng bởi các đội ngũ sản phẩm, dữ liệu và kinh doanh.",
        items: [
          {
            name: "Todd Schecter",
            title: "Trưởng bộ phận Tăng trưởng Tư vấn, Databricks",
            quote: "Một nhà lãnh đạo sản phẩm xuất sắc. Tốc độ đổi mới thật đáng kinh ngạc. Luôn dành thời gian làm việc cùng đội ngũ bán hàng và các khách hàng chiến lược.",
          },
          {
            name: "Thangadurai Elangovan",
            title: "Phó Tổng Giám đốc kiêm Trưởng nhóm Kỹ thuật, Tradebees Bắc Kinh",
            quote: "Có chuyên môn rất vững về dữ liệu lớn. Chủ động giải quyết vấn đề. Bình tĩnh trước áp lực và là một tài sản thực sự của đội ngũ.",
          },
          {
            name: "Lola Han Luo",
            title: "Quản lý sản phẩm cấp cao, FlashIntel",
            quote: "Hiện thân của sự tận tụy. Tràn đầy năng lượng, với kỹ năng quản lý sản phẩm xuất sắc. Tôi tự hào về những gì chúng tôi đã cùng nhau đạt được.",
          },
        ],
      },
      contactCta: {
        eyebrow: "Bạn đang gặp một bài toán sản phẩm khó?",
        title: "Hãy cùng biến quy trình thành sản phẩm mà mọi người thực sự dùng được.",
        button: "Trò chuyện ngay",
      },
    },

    // Product System Map Component
    productSystemMap: {
      eyebrow: "Mô hình vận hành triển khai thực địa",
      title: "Từ vấn đề rối rắm đến sản phẩm được áp dụng.",
      subtitle: "Tôi luôn sát cánh cùng người dùng, dữ liệu và quá trình triển khai, từ khám phá đến production.",
      systemLabel: "Hệ thống sản phẩm",
      systemSub: "Từ vấn đề đến production",
      stages: [
        {
          label: "Hòa nhập",
          title: "Bắt đầu từ bên trong quy trình thực tế",
          description: "Phỏng vấn người dùng, quan sát các điểm chuyển giao và xác định những ràng buộc mà tài liệu yêu cầu thường bỏ sót.",
          detail: "Người dùng + quy trình",
        },
        {
          label: "Mô hình hóa",
          title: "Làm cho dữ liệu trở nên đáng tin cậy",
          description: "Truy vết nguồn dữ liệu, định nghĩa chỉ số và thống nhất mô hình ngữ nghĩa trước khi tự động hóa các quyết định dựa trên đó.",
          detail: "SQL + chỉ số",
        },
        {
          label: "Xây dựng",
          title: "Biến sự mơ hồ thành sản phẩm hoạt động được",
          description: "Tự tay làm prototype giao diện và quy trình AI, sau đó dùng prototype để đẩy nhanh sự đồng thuận và quá trình phát triển.",
          detail: "Prototype + AI",
        },
        {
          label: "Triển khai",
          title: "Đưa giải pháp vào môi trường production",
          description: "Kết nối các hệ thống hiện có, bổ sung đánh giá chất lượng và cơ chế kiểm soát, đồng thời thiết kế lộ trình vận hành quanh sản phẩm.",
          detail: "Tích hợp + độ tin cậy",
        },
        {
          label: "Học hỏi",
          title: "Đo lường mức độ áp dụng, không phải lời khen",
          description: "Theo dõi mức sử dụng và kết quả kinh doanh, quay lại với người dùng và cải tiến cho đến khi sản phẩm trở thành một phần của quy trình.",
          detail: "Áp dụng + tác động",
        },
      ],
    },

    // Tech Constellation Component
    techConstellation: {
      ariaLabel: "Các công nghệ được sử dụng trong hệ thống sản phẩm",
      data: "Dữ liệu tin cậy",
      ai: "Hệ thống AI",
      ship: "Sản phẩm ra mắt",
      technologySuffix: "công nghệ",
    },

    // Work Experience Page
    experiencePage: {
      seo: {
        title: "Kinh nghiệm làm việc - Nandan Priyadarshi | Hơn 10 năm quản lý sản phẩm",
        description: "Hơn 10 năm quản lý sản phẩm AI, dữ liệu và B2B SaaS: Xiaomi, FlashLabs, Wemero, TradeBees và Wipro. Đạt 100% mức sử dụng tại 5 thị trường và tăng 280% giao dịch.",
        keywords: "Kinh nghiệm quản lý sản phẩm, Sự nghiệp PM cấp cao, Quản lý sản phẩm Xiaomi, Phát triển sản phẩm AI, Hồ sơ năng lực quản lý sản phẩm",
      },
      availableBadge: "Sẵn sàng cho cơ hội mới",
      titleLine1: "Một thập kỷ xây dựng",
      titleLine2: "Những sản phẩm có giá trị",
      subtitle: "Từ nền tảng kỹ thuật dữ liệu đến dẫn dắt các nền tảng thương mại ứng dụng AI — mỗi vị trí đều định hình cách tôi đưa sản phẩm ra thị trường với tác động thực sự.",
      stats: [
        { label: "Năm kinh nghiệm", value: "10+" },
        { label: "Sản phẩm đã ra mắt", value: "25+" },
        { label: "Người dùng được tác động", value: "5 triệu+" },
        { label: "Công ty", value: "7" },
      ],
      timelineTitle: "Hành trình sự nghiệp",
      searchPlaceholder: "Tìm theo công ty, vị trí hoặc từ khóa...",
      filterBy: "Lọc theo chuyên môn",
      allFilter: "Tất cả",
      showingCount: "Hiển thị {filtered} / {total} kinh nghiệm",
      clearFilters: "Xóa bộ lọc",
      noResults: "Không tìm thấy kinh nghiệm phù hợp",
      adjustFilters: "Hãy thử điều chỉnh từ khóa tìm kiếm hoặc bộ lọc",
      card: {
        current: "Hiện tại",
        keyMetrics: "Chỉ số chính",
      },
    },

    // Work Experience Detail Page
    experienceDetail: {
      back: "Quay lại",
      currentRole: "Vị trí hiện tại",
      keyImpact: "Tác động chính",
      measurableOutcomes: "Kết quả đo lường được",
      overview: "Tổng quan",
      keyAchievements: "Thành tựu chính",
      impactDelivered: "Tác động mang lại",
      techStack: "Công nghệ & Hệ thống",
      toolsUsed: "Công cụ & công nghệ sử dụng",
      challengesTackled: "Thách thức đã vượt qua",
      problemsSolved: "Vấn đề đã giải quyết",
      businessOutcomes: "Kết quả kinh doanh",
      resultsDelivered: "Kết quả đạt được",
      previous: "Trước",
      next: "Tiếp theo",
      notFoundTitle: "Không tìm thấy kinh nghiệm",
      notFoundDesc: "Kinh nghiệm làm việc bạn đang tìm không tồn tại hoặc đã được di chuyển.",
      viewAllExperiences: "Xem tất cả kinh nghiệm",
    },

    // Case Study Page
    caseStudy: {
      back: "Quay lại",
      theChallenge: "Thách thức",
      understandingPain: "Thấu hiểu vấn đề",
      theApproach: "Cách tiếp cận",
      howWeSolvedIt: "Cách chúng tôi giải quyết",
      theImpact: "Tác động",
      resultsDelivered: "Kết quả đạt được",
      techStack: "Công nghệ sử dụng",
      keyLearnings: "Bài học chính",
      previousCaseStudy: "Nghiên cứu trước",
      nextCaseStudy: "Nghiên cứu tiếp theo",
      interestedWorking: "Bạn muốn hợp tác cùng tôi?",
      letsTalk: "Trò chuyện ngay",
      notFoundTitle: "Không tìm thấy nghiên cứu điển hình",
      notFoundDesc: "Dự án bạn đang tìm không tồn tại.",
      viewAllProjects: "Xem tất cả dự án",
    },

    // Contact Page
    contact: {
      seo: {
        title: "Liên hệ Nandan Priyadarshi | Hợp tác sản phẩm dữ liệu và AI",
        description: "Trao đổi về sản phẩm AI, nền tảng dữ liệu, dự án triển khai thực địa hoặc xây dựng sản phẩm cùng Nandan Priyadarshi.",
        keywords: "Liên hệ Nandan Priyadarshi, Quản lý sản phẩm dữ liệu, Quản lý sản phẩm AI, Nhà xây dựng sản phẩm triển khai thực địa",
      },
      eyebrow: "Mở kênh trao đổi",
      title: "Hãy bắt đầu từ phần khó nhất.",
      subtitle: "Chia sẻ quy trình, ràng buộc dữ liệu hoặc quyết định sản phẩm AI của bạn. Tôi sẽ cùng bạn đi thẳng vào vấn đề.",
      directEmail: "Email trực tiếp",
      copyEmail: "Sao chép địa chỉ email",
      copied: "Đã sao chép email",
      usefulFirstMsg: "Một tin nhắn đầu tiên hữu ích sẽ trả lời",
      location: "Bắc Kinh, Trung Quốc",
      workingBrief: "Bản mô tả công việc",
      workingBriefTitle: "Hãy cho tôi biết quy trình đang tắc ở đâu.",
      workingBriefSub: "Chỉ cần một bản mô tả sơ bộ. Tôi sẽ đặt những câu hỏi cần thiết.",
      topicLegend: "Chúng ta sẽ trao đổi về điều gì?",
      topics: [
        { label: "Sản phẩm AI", subject: "AI product strategy" },
        { label: "Nền tảng dữ liệu", subject: "Data product or platform" },
        { label: "Triển khai thực địa", subject: "Forward-deployed collaboration" },
        { label: "Xây dựng độc lập", subject: "Product build collaboration" },
      ],
      briefPrompts: [
        { question: "Ai đang bị cản trở?", detail: "Người dùng, đội ngũ hoặc khách hàng trong quy trình." },
        { question: "Điều gì đang trục trặc?", detail: "Điểm chuyển giao, ràng buộc dữ liệu hoặc quyết định đang làm chậm họ." },
        { question: "Điều gì cần thay đổi?", detail: "Kết quả khiến sản phẩm xứng đáng được đưa vào sử dụng." },
      ],
      nameLabel: "Tên của bạn",
      namePlaceholder: "Tên của bạn",
      emailLabel: "Email của bạn",
      emailPlaceholder: "you@example.com",
      subjectLabel: "Chủ đề",
      subjectPlaceholder: "Chúng ta nên phân tích điều gì?",
      messageLabel: "Bối cảnh công việc",
      messagePlaceholder: "Ai đang bị cản trở, điều gì đang trục trặc và điều gì cần thay đổi?",
      privacyNote: "Tin nhắn của bạn được gửi thẳng đến hộp thư của tôi. Không có bản tin hay chuỗi email tự động.",
      sendBrief: "Gửi mô tả",
      sending: "Đang gửi",
      sentTitle: "Đã gửi tin nhắn",
      sentDesc: "Cảm ơn bạn đã chia sẻ bối cảnh. Tôi sẽ xem xét vấn đề và phản hồi bạn.",
      sendAnother: "Gửi mô tả khác",
      errorTitle: "Đã xảy ra lỗi",
      errorDesc: "Hãy thử lại hoặc liên hệ trực tiếp qua email.",
      tryAgain: "Thử lại",
      emailDirectly: "Gửi email trực tiếp",
    },

    // Legal Pages
    privacy: {
      seo: {
        title: "Chính sách quyền riêng tư | Nandan Priyadarshi",
        description: "Chính sách quyền riêng tư cho trang web portfolio của Nandan Priyadarshi. Tìm hiểu cách chúng tôi thu thập, sử dụng và bảo vệ thông tin cá nhân của bạn.",
        keywords: "Chính sách quyền riêng tư, Bảo vệ dữ liệu, Nandan Priyadarshi",
      },
      eyebrow: "Pháp lý",
      title: "Chính sách quyền riêng tư",
      lastUpdated: "Cập nhật lần cuối: Tháng 3 năm 2024",
      description: "Quyền riêng tư của bạn rất quan trọng với chúng tôi. Dưới đây là cách chúng tôi xử lý dữ liệu của bạn.",
      intro: "Chính sách quyền riêng tư này mô tả cách Nandan Priyadarshi (\"chúng tôi\") thu thập, sử dụng và chia sẻ thông tin về bạn khi bạn truy cập trang web portfolio của chúng tôi. Khi sử dụng trang web, bạn đồng ý với việc thu thập và sử dụng thông tin theo chính sách này.",
      footerNote: "Chính sách quyền riêng tư này có hiệu lực từ ngày 1 tháng 1 năm 2026 và sẽ tiếp tục có hiệu lực, ngoại trừ các thay đổi trong tương lai đối với điều khoản, vốn sẽ có hiệu lực ngay sau khi được đăng trên trang này.",
      sections: [
        {
          title: "Thông tin chúng tôi thu thập",
          content: "Chúng tôi thu thập thông tin bạn cung cấp trực tiếp, chẳng hạn khi bạn liên hệ qua biểu mẫu liên hệ. Thông tin này có thể bao gồm tên, địa chỉ email và nội dung tin nhắn của bạn. Chúng tôi cũng tự động thu thập một số thông tin khi bạn truy cập trang web, bao gồm địa chỉ IP, loại trình duyệt và các trang đã truy cập.",
        },
        {
          title: "Cách chúng tôi sử dụng thông tin của bạn",
          content: "Chúng tôi sử dụng thông tin thu thập được để phản hồi yêu cầu của bạn, cải thiện trang web và dịch vụ, gửi cập nhật cho bạn nếu bạn đã đồng ý nhận, đồng thời phân tích lưu lượng truy cập và hành vi sử dụng trang web. Chúng tôi không bán thông tin cá nhân của bạn cho bên thứ ba.",
        },
        {
          title: "Bảo mật dữ liệu",
          content: "Chúng tôi áp dụng các biện pháp kỹ thuật và tổ chức phù hợp để bảo vệ thông tin cá nhân của bạn khỏi truy cập trái phép, thay đổi, tiết lộ hoặc phá hủy. Tuy nhiên, không có phương thức truyền tải nào qua Internet an toàn tuyệt đối 100%.",
        },
        {
          title: "Cookie & Theo dõi",
          content: "Trang web của chúng tôi có thể sử dụng cookie và các công nghệ theo dõi tương tự để nâng cao trải nghiệm duyệt web của bạn. Bạn có thể kiểm soát cookie thông qua cài đặt trình duyệt. Chúng tôi sử dụng các công cụ phân tích để hiểu cách khách truy cập tương tác với trang web.",
        },
        {
          title: "Quyền của bạn",
          content: "Bạn có quyền truy cập, chỉnh sửa hoặc xóa thông tin cá nhân của mình. Bạn cũng có thể từ chối nhận thông tin tiếp thị bất cứ lúc nào. Để thực hiện các quyền này, vui lòng liên hệ với chúng tôi qua thông tin được cung cấp bên dưới.",
        },
        {
          title: "Liên hệ với chúng tôi",
          content: "Nếu bạn có bất kỳ câu hỏi nào về Chính sách quyền riêng tư này hoặc cách chúng tôi xử lý dữ liệu, vui lòng liên hệ qua nandanpriyadarshi298@gmail.com. Chúng tôi sẽ phản hồi yêu cầu của bạn trong khoảng thời gian hợp lý.",
        },
      ],
    },

    terms: {
      seo: {
        title: "Điều khoản & Điều kiện | Nandan Priyadarshi",
        description: "Điều khoản và điều kiện sử dụng trang web portfolio của Nandan Priyadarshi. Vui lòng đọc kỹ các điều khoản này trước khi sử dụng dịch vụ của chúng tôi.",
        keywords: "Điều khoản và Điều kiện, Điều khoản pháp lý, Nandan Priyadarshi",
      },
      eyebrow: "Pháp lý",
      title: "Điều khoản & Điều kiện",
      lastUpdated: "Cập nhật lần cuối: Tháng 1 năm 2026",
      description: "Vui lòng đọc kỹ các điều khoản này trước khi sử dụng trang web của chúng tôi.",
      intro: "Chào mừng bạn đến với trang web portfolio của Nandan Priyadarshi. Các điều khoản và điều kiện này quy định các nguyên tắc sử dụng trang web. Khi truy cập trang web này, chúng tôi xem như bạn chấp nhận toàn bộ các điều khoản và điều kiện. Vui lòng không tiếp tục sử dụng trang web nếu bạn không chấp nhận tất cả các điều khoản và điều kiện được nêu trên trang này.",
      footerNote: "Các điều khoản và điều kiện này có hiệu lực từ ngày 1 tháng 1 năm 2026 và sẽ tiếp tục có hiệu lực, ngoại trừ các thay đổi trong tương lai đối với điều khoản.",
      agreementTitle: "Đồng ý với Điều khoản",
      agreementText: "Khi sử dụng trang web này, bạn xác nhận chấp nhận các điều khoản. Nếu không đồng ý, vui lòng không sử dụng trang web của chúng tôi. Việc bạn tiếp tục sử dụng trang web sau khi các thay đổi được đăng tải đồng nghĩa với việc bạn chấp nhận những thay đổi đó.",
      questionsContact: "Nếu bạn có bất kỳ câu hỏi nào về Điều khoản & Điều kiện này, vui lòng liên hệ với chúng tôi qua",
      sections: [
        {
          title: "Sử dụng trang web",
          content: "Khi truy cập trang web này, bạn đồng ý chỉ sử dụng trang web cho các mục đích hợp pháp và theo cách không xâm phạm quyền của người khác hoặc hạn chế việc sử dụng trang web của họ. Bạn chỉ được xem, tải xuống và in nội dung từ trang web này cho mục đích cá nhân, phi thương mại.",
        },
        {
          title: "Sở hữu trí tuệ",
          content: "Toàn bộ nội dung trên trang web này, bao gồm nhưng không giới hạn ở văn bản, đồ họa, logo, hình ảnh và phần mềm, là tài sản của Nandan Priyadarshi và được bảo vệ bởi luật sở hữu trí tuệ. Bạn không được sao chép, phân phối hoặc tạo tác phẩm phái sinh khi chưa có sự đồng ý trước bằng văn bản.",
        },
        {
          title: "Tuyên bố miễn trừ trách nhiệm",
          content: "Thông tin trên trang web này chỉ nhằm mục đích cung cấp thông tin chung. Mặc dù chúng tôi luôn cố gắng cập nhật và đảm bảo thông tin chính xác, chúng tôi không đưa ra bất kỳ cam kết hay bảo đảm nào về tính đầy đủ, chính xác, độ tin cậy hoặc tính khả dụng của trang web hay thông tin trên đó.",
        },
        {
          title: "Giới hạn trách nhiệm",
          content: "Trong mọi trường hợp, Nandan Priyadarshi sẽ không chịu trách nhiệm đối với bất kỳ thiệt hại gián tiếp, ngẫu nhiên, đặc biệt, hệ quả hoặc mang tính trừng phạt nào phát sinh từ hoặc liên quan đến việc bạn sử dụng trang web này. Giới hạn này được áp dụng bất kể thiệt hại phát sinh từ vi phạm hợp đồng, hành vi dân sự sai trái hay bất kỳ cơ sở pháp lý nào khác.",
        },
        {
          title: "Thay đổi điều khoản",
          content: "Chúng tôi có quyền sửa đổi các điều khoản và điều kiện này bất cứ lúc nào mà không cần thông báo trước. Việc bạn tiếp tục sử dụng trang web sau bất kỳ thay đổi nào đồng nghĩa với việc chấp nhận các điều khoản mới. Chúng tôi khuyến khích bạn thường xuyên xem lại trang này để cập nhật.",
        },
        {
          title: "Luật điều chỉnh",
          content: "Các điều khoản và điều kiện này được điều chỉnh và giải thích theo pháp luật hiện hành, và bạn đồng ý không hủy ngang việc chịu thẩm quyền riêng của tòa án tại khu vực đó. Mọi tranh chấp phát sinh từ các điều khoản này sẽ được giải quyết thông qua các kênh pháp lý phù hợp.",
        },
      ],
    },

    // 404 Page
    notFound: {
      seo: {
        title: "404 - Không tìm thấy trang | Nandan Priyadarshi",
        description: "Trang bạn đang tìm không tồn tại. Quay lại trang chủ portfolio của Nandan Priyadarshi.",
      },
      title: "Không tìm thấy trang",
      description: "Trang bạn đang tìm không tồn tại hoặc đã được di chuyển. Hãy để chúng tôi đưa bạn quay lại đúng hướng.",
      backHome: "Về trang chủ",
      contactMe: "Liên hệ với tôi",
      quickLinks: "Liên kết nhanh:",
      home: "Trang chủ",
      experience: "Kinh nghiệm",
      contact: "Liên hệ",
      privacy: "Quyền riêng tư",
    },

    // Command Palette
    commandPalette: {
      placeholder: "Nhập lệnh hoặc tìm kiếm...",
      noResults: "Không tìm thấy lệnh nào",
      navigate: "Di chuyển",
      select: "Chọn",
      close: "Đóng",
      commands: {
        home: "Về trang chủ",
        experience: "Xem kinh nghiệm",
        contact: "Liên hệ với tôi",
        resume: "Tải sơ yếu lý lịch",
        github: "Mở GitHub",
        linkedin: "Mở LinkedIn",
        twitter: "Mở X (Twitter)",
      },
    },

    // Error Boundary
    errorBoundary: {
      title: "Rất tiếc! Đã xảy ra lỗi",
      description: "Đừng lo, đây không phải lỗi của bạn. Chúng tôi đã nhận được thông báo và sẽ sớm khắc phục.",
      technicalDetails: "Chi tiết kỹ thuật (chỉ dành cho môi trường phát triển)",
      tryAgain: "Thử lại",
      goHome: "Về trang chủ",
      helpText: "Nếu lỗi này tiếp tục xảy ra, vui lòng",
      contactMe: "liên hệ với tôi",
    },
  },
};
